import { DialogComponent } from '@theme/dialog';

class KbBrandFilm extends DialogComponent {
  requiredRefs = ['dialog', 'video'];

  constructor() {
    super();

    this.eventNames = {
      open: 'kb:brand-film:open',
    };

    this.selectors = {
      trigger: '[data-kb-open-brand-film]',
      link: 'a[href]',
    };

    this.attributeNames = {
      paused: 'data-paused',
      blocked: 'data-playback-blocked',
      loadedSource: 'data-loaded-source',
    };

    this.customPropertyNames = {
      progress: '--kb-brand-film-progress',
    };

    this.labels = {
      play: 'Play video',
      pause: 'Pause video',
    };

    this.desktopViewport = window.matchMedia('(min-width: 750px)');

    this.progressFrame = null;

    this.listenerController = null;

    const inheritedCloseDialog = this.closeDialog;

    this.closeDialog = async () => {
      this.stopProgressLoop();
      this.resetPlayback();
      await inheritedCloseDialog();
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.listenerController = new AbortController();
    const { signal } = this.listenerController;

    const { video, control } = this.refs;

    this.readLabels();

    document.addEventListener('click', this.handleDocumentClick, { signal });
    document.addEventListener(this.eventNames.open, this.handleOpenRequest, { signal });

    this.addEventListener('dialog:open', this.handleDialogOpen, { signal });

    video.addEventListener('play', this.handlePlaybackChange, { signal });
    video.addEventListener('pause', this.handlePlaybackChange, { signal });
    video.addEventListener('ended', this.handlePlaybackEnd, { signal });
    video.addEventListener('click', this.togglePlayback, { signal });

    if (control) {
      control.addEventListener('click', this.togglePlayback, { signal });
    }

    if (window.Shopify?.designMode) {
      const section = this.closest('.shopify-section');

      if (section) {
        section.addEventListener('shopify:section:select', this.handleOpenRequest, { signal });
        section.addEventListener('shopify:section:deselect', this.closeDialog, { signal });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.stopProgressLoop();
    this.listenerController?.abort();
    this.listenerController = null;
  }

  get triggerAnchor() {
    return this.dataset.triggerAnchor || '#kb-brand-film';
  }

  readLabels() {
    this.labels = {
      play: this.dataset.labelPlay || this.labels.play,
      pause: this.dataset.labelPause || this.labels.pause,
    };
  }

  handleDocumentClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!this.findTrigger(event.target)) return;

    event.preventDefault();
    this.open();
  };

  findTrigger(target) {
    if (!(target instanceof Element)) return null;

    const flagged = target.closest(this.selectors.trigger);
    if (flagged) return flagged;

    const link = target.closest(this.selectors.link);
    if (!(link instanceof HTMLAnchorElement)) return null;
    if (link.hash !== this.triggerAnchor) return null;
    if (!this.isSamePagePath(link)) return null;
    if (link.target && link.target !== '_self') return null;
    if (link.hasAttribute('download')) return null;

    return link;
  }

  isSamePagePath(link) {
    if (link.host !== window.location.host) return false;

    const linkPath = link.pathname.replace(/\/$/, '');
    const currentPath = window.location.pathname.replace(/\/$/, '');

    return linkPath === currentPath;
  }

  handleOpenRequest = () => {
    this.open();
  };

  open() {
    this.startPlayback();
    this.showDialog();
  }

  startPlayback() {
    const { video } = this.refs;
    const source = this.sourceForViewport();

    if (!source) return;

    if (video.getAttribute(this.attributeNames.loadedSource) !== source) {
      video.setAttribute(this.attributeNames.loadedSource, source);
      video.src = source;
      video.load();
    }

    video.muted = false;

    const playback = video.play();

    if (playback && typeof playback.catch === 'function') {
      playback.catch(() => {
        video.setAttribute(this.attributeNames.blocked, '');
        this.applyPoster();
        this.syncControl();
      });
    }
  }

  sourceForViewport() {
    const { video } = this.refs;
    const desktopSource = video.dataset.videoDesktop || '';
    const mobileSource = video.dataset.videoMobile || '';

    if (this.desktopViewport.matches) {
      return desktopSource || mobileSource;
    }

    return mobileSource || desktopSource;
  }

  handleDialogOpen = () => {
    const { video } = this.refs;

    if (video.paused) {
      this.applyPoster();
    } else {
      this.startProgressLoop();
    }

    this.syncControl();
  };

  handlePlaybackChange = () => {
    const { video } = this.refs;

    if (video.paused) {
      this.stopProgressLoop();
    } else {
      video.removeAttribute(this.attributeNames.blocked);
      this.startProgressLoop();
    }

    this.syncControl();
  };

  handlePlaybackEnd = () => {
    this.stopProgressLoop();

    if (this.dataset.onEnd === 'close') {
      this.closeDialog();
      return;
    }

    this.style.setProperty(this.customPropertyNames.progress, '1');
    this.syncControl();
  };

  togglePlayback = () => {
    const { video } = this.refs;

    if (!video.paused) {
      video.pause();
      return;
    }

    video.muted = false;

    const playback = video.play();

    if (playback && typeof playback.catch === 'function') {
      playback.catch(() => {
        video.setAttribute(this.attributeNames.blocked, '');
      });
    }
  };

  applyPoster() {
    const { video } = this.refs;
    const poster = video.dataset.poster;

    if (!poster || video.poster) return;

    video.poster = poster;
  }

  syncControl() {
    const { video, control } = this.refs;
    const isPaused = video.paused;

    this.toggleAttribute(this.attributeNames.paused, isPaused);

    if (!control) return;

    control.setAttribute('aria-label', isPaused ? this.labels.play : this.labels.pause);
  }

  startProgressLoop() {
    this.stopProgressLoop();

    const step = () => {
      const { video } = this.refs;
      const { duration, currentTime } = video;

      if (Number.isFinite(duration) && duration > 0) {
        this.style.setProperty(this.customPropertyNames.progress, String(currentTime / duration));
      }

      this.progressFrame = requestAnimationFrame(step);
    };

    this.progressFrame = requestAnimationFrame(step);
  }

  stopProgressLoop() {
    if (this.progressFrame === null) return;

    cancelAnimationFrame(this.progressFrame);
    this.progressFrame = null;
  }

  resetPlayback() {
    const { video } = this.refs;

    video.pause();
    video.currentTime = 0;
    video.removeAttribute(this.attributeNames.blocked);
    this.style.setProperty(this.customPropertyNames.progress, '0');
  }
}

if (!customElements.get('kb-brand-film')) {
  customElements.define('kb-brand-film', KbBrandFilm);
}

export { KbBrandFilm };
